// ---------------------------------------------------------------------------
// Occlusion — Zone-based PVS (Potentially Visible Set) + geometry batching.
// This is how CS 1.6/Source engine handles visibility: precomputed zone
// connectivity, toggling object.visible per zone based on camera position.
// ---------------------------------------------------------------------------

import * as THREE from 'three';
import { MeshBVH, acceleratedRaycast } from 'three-mesh-bvh';

// Install BVH accelerated raycast
THREE.Mesh.prototype.raycast = acceleratedRaycast;

// ---------------------------------------------------------------------------
// Visibility Zone definitions
// ---------------------------------------------------------------------------

export interface VisibilityZone {
  label: string;
  /** AABB min corner [x, y, z]. */
  min: [number, number, number];
  /** AABB max corner [x, y, z]. */
  max: [number, number, number];
  /** Indices of zones visible FROM this zone. */
  visibleZones: number[];
}

// ---------------------------------------------------------------------------
// de_outpost_v6 visibility zones (Phase Integration — modular FOB rebuild,
// arenaHalf: 80). 12 zones aligned with the 10 canonical zone modules in
// scripts/blender/outpost_v6_*.py plus separate Lower/Upper splits for the
// multi-storey hero buildings (A Site Command Center, B Site Warehouse).
// Zone footprints derived from each module's ZONE_CENTER and the hero
// building bbox dimensions in PLAN.md §8.
// ---------------------------------------------------------------------------

export const DE_OUTPOST_V6_VISIBILITY_ZONES: VisibilityZone[] = [
  {
    label: 'T Spawn / Staging Yard',
    min: [-26, -2, 52], max: [26, 12, 80],
    visibleZones: [0, 1, 2, 3], // self, Long A, Mid, B Tunnels
  },
  {
    label: 'Long A',
    min: [-66, -2, -8], max: [-34, 12, 32],
    visibleZones: [0, 1, 2, 4, 6], // T Spawn, self, Mid, A Site Lower, Short A
  },
  {
    label: 'Mid Courtyard',
    min: [-15, -2, 5], max: [15, 12, 35],
    visibleZones: [0, 1, 2, 3, 6], // T Spawn, Long A, self, B Tunnels, Short A
  },
  {
    label: 'B Tunnels',
    min: [34, -2, 5], max: [66, 12, 35],
    visibleZones: [0, 2, 3, 7, 8], // T Spawn, Mid, self, B Site Floor, B Site Catwalk
  },
  {
    label: 'A Site Lower (Command Center)',
    min: [-66, -2, -50], max: [-32, 4, -22],
    visibleZones: [1, 4, 5, 6, 9], // Long A, self, A Site Upper, Short A, CT-A Tunnel
  },
  {
    label: 'A Site Upper (Balcony / Roof)',
    min: [-58, 3, -50], max: [-32, 14, -28],
    visibleZones: [4, 5, 6, 9, 11], // A Site Lower, self, Short A, CT-A Tunnel, CT Spawn
  },
  {
    label: 'Short A Connector',
    min: [-26, -2, -2], max: [-4, 12, 22],
    visibleZones: [1, 2, 4, 5, 6, 9], // Long A, Mid, A Site Lower, A Site Upper, self, CT-A Tunnel
  },
  {
    label: 'B Site Floor (Warehouse)',
    min: [36, -2, -26], max: [64, 4, 0],
    visibleZones: [3, 7, 8, 10, 11], // B Tunnels, self, B Site Catwalk, Drainage, CT Spawn
  },
  {
    label: 'B Site Catwalk (Warehouse Upper)',
    min: [36, 3, -26], max: [64, 12, 0],
    visibleZones: [3, 7, 8, 10, 11], // B Tunnels, B Site Floor, self, Drainage, CT Spawn
  },
  {
    label: 'CT-A Tunnel',
    min: [-32, -3, -22], max: [-8, 4, -2],
    visibleZones: [4, 5, 6, 9, 11], // A Site Lower, A Site Upper, Short A, self, CT Spawn
  },
  {
    label: 'Drainage Channel',
    min: [10, -3, -37], max: [32, 4, -13],
    visibleZones: [7, 8, 10, 11], // B Site Floor, B Site Catwalk, self, CT Spawn
  },
  {
    label: 'CT Spawn / HQ Compound',
    min: [-26, -2, -80], max: [26, 14, -50],
    visibleZones: [5, 7, 8, 9, 10, 11], // A Site Upper, B Site Floor, B Site Catwalk, CT-A Tunnel, Drainage, self
  },
];

// ---------------------------------------------------------------------------
// Map-to-zones lookup
// ---------------------------------------------------------------------------

export function getVisibilityZonesForMap(mapId: string): VisibilityZone[] {
  if (mapId === 'de_outpost_v6') return DE_OUTPOST_V6_VISIBILITY_ZONES;
  // Unknown maps: empty list so initPVS becomes a no-op (all objects visible).
  return [];
}

// ---------------------------------------------------------------------------
// PVS Engine
// ---------------------------------------------------------------------------

/**
 * Single-zone objects grouped by their one zone index. Batching operates on
 * this bucket so merged geometry stays coherent per zone.
 */
const zoneObjects = new Map<number, THREE.Object3D[]>();

/**
 * Objects whose world bounds overlap more than one visibility zone. Tracked
 * separately so they are not batched (batching collapses per-zone meshes)
 * and are visibility-toggled by checking all their zones against the PVS.
 */
interface MultiZoneEntry {
  obj: THREE.Object3D;
  zones: number[];
}
const multiZoneObjects: MultiZoneEntry[] = [];

/** Batched meshes created by batchZoneGeometry — tracked for full disposal. */
const batchedMeshes: THREE.Mesh[] = [];

/** Current active zone index (-1 = unknown, show everything). */
let currentZone = -1;

/** Reference to the zones array being used. */
let activeZones: VisibilityZone[] = [];

/**
 * F-MAP-012: Cache of the last applied visible-zone set so updatePVS only
 * toggles the symmetric difference between old and new on zone change.
 * Reset when initPVS reseeds zones or when the toggling pass falls back to
 * the full re-apply path (invalid camera zone).
 */
let lastVisibleSet: Set<number> | null = null;

/**
 * Determine which zone a point is in.
 */
function getZoneForPoint(x: number, y: number, z: number): number {
  for (let i = 0; i < activeZones.length; i++) {
    const zone = activeZones[i];
    if (
      x >= zone.min[0] && x <= zone.max[0] &&
      y >= zone.min[1] && y <= zone.max[1] &&
      z >= zone.min[2] && z <= zone.max[2]
    ) {
      return i;
    }
  }
  return -1; // Outside all zones
}

const _tmpBox = new THREE.Box3();

/**
 * Find every visibility zone whose AABB overlaps the given world-space box.
 * Used instead of origin-point classification so a large structure that
 * straddles two zones is visible whenever either zone is the camera's PVS.
 */
function getZonesForBox(box: THREE.Box3): number[] {
  const overlapping: number[] = [];
  for (let i = 0; i < activeZones.length; i++) {
    const zone = activeZones[i];
    if (
      box.max.x >= zone.min[0] && box.min.x <= zone.max[0] &&
      box.max.y >= zone.min[1] && box.min.y <= zone.max[1] &&
      box.max.z >= zone.min[2] && box.min.z <= zone.max[2]
    ) {
      overlapping.push(i);
    }
  }
  return overlapping;
}

/**
 * F-MAP-016: Multi-storey buildings (de_outpost_v6 A Site Lower/Upper at
 * y=-2..4 / 3..14, B Site Floor/Catwalk at y=-2..4 / 3..12) overlap on a
 * thin Y-band at the storey seam. A horizontal ceiling slab whose bbox
 * extends slightly above its lower-zone ceiling otherwise ends up
 * classified into BOTH zones — and renders on top of the upper floor when
 * the camera moves up there.
 *
 * Prune zones whose Y overlap with the entry's Y range is a sub-1m "seam"
 * AND the entry's Y centre is well outside that zone's Y range. Larger
 * overlaps (an actual staircase that spans both storeys, or a tall building
 * shell) are retained. If pruning would empty the list, fall back to the
 * original list — better to over-show than vanish.
 */
function pruneCrossStoreyZones(box: THREE.Box3, zones: number[]): number[] {
  if (zones.length < 2) return zones;
  const SEAM_OVERLAP_THRESHOLD = 1.0; // metres
  const entryCenterY = (box.min.y + box.max.y) * 0.5;
  const entryHeight = box.max.y - box.min.y;
  const kept: number[] = [];
  for (const zoneIdx of zones) {
    const zone = activeZones[zoneIdx];
    const yMin = Math.max(box.min.y, zone.min[1]);
    const yMax = Math.min(box.max.y, zone.max[1]);
    const overlap = Math.max(0, yMax - yMin);

    // Always keep the zone the entry's Y centre lies inside.
    if (entryCenterY >= zone.min[1] && entryCenterY <= zone.max[1]) {
      kept.push(zoneIdx);
      continue;
    }

    // Drop seam-only zones: small absolute overlap AND the entry extends
    // well past this zone's Y range (so the entry "really lives" elsewhere).
    if (overlap < SEAM_OVERLAP_THRESHOLD && entryHeight > overlap * 2) {
      continue;
    }
    kept.push(zoneIdx);
  }
  return kept.length > 0 ? kept : zones;
}

/**
 * Initialize the PVS system.
 * Call after all map geometry is loaded.
 *
 * Assignment is bounds-based: each mesh is classified by the set of zones its
 * world-space AABB overlaps. A mesh overlapping a single zone is bucketed into
 * that zone (batchable). A mesh overlapping multiple zones is tracked in the
 * multi-zone list and is shown whenever ANY of its zones is visible — this
 * prevents large structures (buildings, roofs, catwalks) from disappearing
 * wholesale when their origin happens to fall into an invisible zone.
 */
export function initPVS(
  scene: THREE.Scene,
  zones: VisibilityZone[],
): void {
  activeZones = zones;
  zoneObjects.clear();
  multiZoneObjects.length = 0;

  // Initialize zone buckets
  for (let i = 0; i < zones.length; i++) {
    zoneObjects.set(i, []);
  }

  // Assign each scene object to the zones its bounds overlap.
  scene.traverse((obj) => {
    if (obj === scene) return;
    if (obj.type === 'Scene') return;

    // Skip lights — they should always be active
    if (obj instanceof THREE.Light) return;

    // Skip objects without geometry (groups, etc.)
    if (!(obj instanceof THREE.Mesh) && !(obj instanceof THREE.LOD)) return;

    // Box3.setFromObject walks descendants too, so we clear it per-call.
    // For leaf meshes the cost is bounded by vertex count of that one mesh.
    _tmpBox.makeEmpty();
    _tmpBox.setFromObject(obj);
    if (_tmpBox.isEmpty()) return;

    const rawOverlap = getZonesForBox(_tmpBox);
    if (rawOverlap.length === 0) return; // Outside all zones — always visible

    // F-MAP-016: prune cross-storey seam overlaps so a lower-floor ceiling
    // slab doesn't render on top of the upper floor.
    const overlapping = pruneCrossStoreyZones(_tmpBox, rawOverlap);

    if (overlapping.length === 1) {
      const zoneIdx = overlapping[0];
      obj.userData.pvsZone = zoneIdx;
      obj.userData.pvsZones = overlapping;
      zoneObjects.get(zoneIdx)!.push(obj);
    } else {
      obj.userData.pvsZone = overlapping[0];
      obj.userData.pvsZones = overlapping;
      multiZoneObjects.push({ obj, zones: overlapping });
    }
  });

  currentZone = -1;
  lastVisibleSet = null;
}

/**
 * Update visibility based on camera position.
 * Call every frame. Only updates when the player changes zones.
 */
export function updatePVS(camera: THREE.Camera): void {
  if (activeZones.length === 0) return;

  const pos = camera.position;
  const newZone = getZoneForPoint(pos.x, pos.y, pos.z);

  // Only update when zone changes
  if (newZone === currentZone) return;
  currentZone = newZone;

  if (newZone === -1 || newZone < 0 || newZone >= activeZones.length) {
    // Outside/invalid zone — show everything as fallback
    for (const [, objects] of zoneObjects) {
      for (const obj of objects) {
        obj.visible = true;
      }
    }
    for (const entry of multiZoneObjects) {
      entry.obj.visible = true;
    }
    // F-MAP-012: drop the cached set — next valid-zone entry needs a full
    // toggle pass because we just unconditionally turned everything on.
    lastVisibleSet = null;
    return;
  }

  // Get visible zone set
  const visibleSet = new Set(activeZones[newZone].visibleZones);

  // F-MAP-012: Symmetric-difference toggle path.
  //   - First call after a reset (or after the show-everything fallback):
  //     run the full per-zone loop so multi-zone state is sane.
  //   - Subsequent calls: only re-toggle zone buckets that flipped, and only
  //     re-evaluate multi-zone entries whose zone overlap intersects the diff.
  if (lastVisibleSet) {
    // Compute the changed zone indices (added or removed).
    const changedZones: number[] = [];
    for (const z of visibleSet) {
      if (!lastVisibleSet.has(z)) changedZones.push(z);
    }
    for (const z of lastVisibleSet) {
      if (!visibleSet.has(z)) changedZones.push(z);
    }

    if (changedZones.length === 0) {
      // Visibility set identical despite zone change — nothing to do.
      lastVisibleSet = visibleSet;
      return;
    }

    // Toggle only the zone buckets whose visibility flipped.
    for (const zoneIdx of changedZones) {
      const objects = zoneObjects.get(zoneIdx);
      if (!objects) continue;
      const visible = visibleSet.has(zoneIdx);
      for (const obj of objects) {
        obj.visible = visible;
      }
    }

    // Multi-zone objects: only re-evaluate entries that touch a flipped zone.
    const changedZoneSet = new Set(changedZones);
    for (const entry of multiZoneObjects) {
      let touched = false;
      for (const z of entry.zones) {
        if (changedZoneSet.has(z)) { touched = true; break; }
      }
      if (!touched) continue;
      let visible = false;
      for (const z of entry.zones) {
        if (visibleSet.has(z)) { visible = true; break; }
      }
      entry.obj.visible = visible;
    }
  } else {
    // Toggle single-zone buckets wholesale — cheap, one flag flip per zone.
    for (const [zoneIdx, objects] of zoneObjects) {
      const visible = visibleSet.has(zoneIdx);
      for (const obj of objects) {
        obj.visible = visible;
      }
    }

    // Multi-zone objects: visible if ANY overlapping zone is in the PVS.
    for (const entry of multiZoneObjects) {
      let visible = false;
      for (const z of entry.zones) {
        if (visibleSet.has(z)) { visible = true; break; }
      }
      entry.obj.visible = visible;
    }
  }

  lastVisibleSet = visibleSet;
}

/**
 * Get the current zone index (for debug display).
 */
export function getCurrentZone(): number {
  return currentZone;
}

/**
 * Get the zone label for a given index.
 */
export function getZoneLabel(index: number): string {
  if (index < 0 || index >= activeZones.length) return 'Unknown';
  return activeZones[index].label;
}

// ---------------------------------------------------------------------------
// Geometry Batching — merge static brushes per area
// ---------------------------------------------------------------------------

/**
 * Merge all static meshes within a zone into a single BufferGeometry.
 * Reduces draw calls significantly for large zones.
 *
 * Call after initPVS. Non-destructive — creates merged meshes alongside
 * originals (originals are hidden).
 */
export function batchZoneGeometry(scene: THREE.Scene): void {
  for (const [zoneIdx, objects] of zoneObjects) {
    // Group meshes by material for batching
    const materialGroups = new Map<string, THREE.Mesh[]>();

    for (const obj of objects) {
      if (!(obj instanceof THREE.Mesh)) continue;
      if (!obj.geometry) continue;
      if (obj.userData.isBatched) continue;
      if (obj instanceof THREE.BatchedMesh) continue;

      // Use material UUID as group key
      const mat = obj.material as THREE.Material;
      const key = mat.uuid;

      if (!materialGroups.has(key)) {
        materialGroups.set(key, []);
      }
      materialGroups.get(key)!.push(obj);
    }

    // Merge each material group
    for (const [, meshes] of materialGroups) {
      if (meshes.length < 3) continue; // Only batch 3+ meshes

      const geometries: THREE.BufferGeometry[] = [];
      for (const mesh of meshes) {
        const geo = mesh.geometry.clone();
        mesh.updateMatrixWorld(true);
        geo.applyMatrix4(mesh.matrixWorld);
        geometries.push(geo);
      }

      try {
        const merged = mergeBufferGeometries(geometries);
        if (merged) {
          const batchedMesh = new THREE.Mesh(merged, meshes[0].material);
          batchedMesh.castShadow = true;
          batchedMesh.receiveShadow = true;
          batchedMesh.userData.pvsZone = zoneIdx;
          batchedMesh.userData.isBatched = true;

          scene.add(batchedMesh);
          batchedMeshes.push(batchedMesh);

          // Add to zone objects
          zoneObjects.get(zoneIdx)!.push(batchedMesh);

          // Hide originals and remove from raycasting layer so hitscan
          // rays don't collide with invisible (batched-away) geometry.
          for (const mesh of meshes) {
            mesh.visible = false;
            mesh.layers.disableAll();
            mesh.userData.batchedAway = true;
          }
        }
      } catch {
        // Merge can fail with incompatible geometries — skip silently
      }

      // Cleanup cloned geometries
      for (const geo of geometries) {
        geo.dispose();
      }
    }
  }
}

/**
 * Simple buffer geometry merge (positions + normals + uvs).
 */
function mergeBufferGeometries(
  geometries: THREE.BufferGeometry[],
): THREE.BufferGeometry | null {
  if (geometries.length === 0) return null;

  let totalVertices = 0;
  let totalIndices = 0;
  const hasNormals = geometries[0].getAttribute('normal') !== undefined;
  const hasUVs = geometries[0].getAttribute('uv') !== undefined;

  for (const geo of geometries) {
    totalVertices += geo.getAttribute('position').count;
    if (geo.index) totalIndices += geo.index.count;
    else totalIndices += geo.getAttribute('position').count;
  }

  const positions = new Float32Array(totalVertices * 3);
  const normals = hasNormals ? new Float32Array(totalVertices * 3) : null;
  const uvs = hasUVs ? new Float32Array(totalVertices * 2) : null;
  const indices = new Uint32Array(totalIndices);

  let vertexOffset = 0;
  let indexOffset = 0;

  for (const geo of geometries) {
    const posAttr = geo.getAttribute('position');
    const normAttr = hasNormals ? geo.getAttribute('normal') : null;
    const uvAttr = hasUVs ? geo.getAttribute('uv') : null;

    // Copy positions
    for (let i = 0; i < posAttr.count; i++) {
      positions[(vertexOffset + i) * 3] = posAttr.getX(i);
      positions[(vertexOffset + i) * 3 + 1] = posAttr.getY(i);
      positions[(vertexOffset + i) * 3 + 2] = posAttr.getZ(i);
    }

    // Copy normals
    if (normals && normAttr) {
      for (let i = 0; i < normAttr.count; i++) {
        normals[(vertexOffset + i) * 3] = normAttr.getX(i);
        normals[(vertexOffset + i) * 3 + 1] = normAttr.getY(i);
        normals[(vertexOffset + i) * 3 + 2] = normAttr.getZ(i);
      }
    }

    // Copy UVs
    if (uvs && uvAttr) {
      for (let i = 0; i < uvAttr.count; i++) {
        uvs[(vertexOffset + i) * 2] = uvAttr.getX(i);
        uvs[(vertexOffset + i) * 2 + 1] = uvAttr.getY(i);
      }
    }

    // Copy indices (offset by vertex count) — check for Uint32 overflow
    const MAX_UINT32 = 0xFFFFFFFF;
    if (geo.index) {
      for (let i = 0; i < geo.index.count; i++) {
        const idx = geo.index.getX(i) + vertexOffset;
        if (idx > MAX_UINT32) {
          console.warn('Occlusion: index overflow during geometry merge — clamping');
          indices[indexOffset + i] = MAX_UINT32;
        } else {
          indices[indexOffset + i] = idx;
        }
      }
      indexOffset += geo.index.count;
    } else {
      for (let i = 0; i < posAttr.count; i++) {
        const idx = vertexOffset + i;
        if (idx > MAX_UINT32) {
          console.warn('Occlusion: index overflow during geometry merge — clamping');
          indices[indexOffset + i] = MAX_UINT32;
        } else {
          indices[indexOffset + i] = idx;
        }
      }
      indexOffset += posAttr.count;
    }

    vertexOffset += posAttr.count;
  }

  const merged = new THREE.BufferGeometry();
  merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  if (normals) merged.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  if (uvs) merged.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  merged.setIndex(new THREE.BufferAttribute(indices, 1));

  return merged;
}

// ---------------------------------------------------------------------------
// BVH-accelerated spatial queries (replace brute-force collider loop)
// ---------------------------------------------------------------------------

let collisionBVH: MeshBVH | null = null;
let collisionMesh: THREE.Mesh | null = null;

function readBooleanLike(value: unknown): boolean | undefined {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return undefined;
}

function isExplicitCollisionOptOut(value: unknown): boolean {
  return value === false || value === 'false';
}

function getCollisionDirective(obj: THREE.Object3D): boolean | undefined {
  for (let cur: THREE.Object3D | null = obj; cur; cur = cur.parent) {
    const rawCollision = cur.userData.cp_collision ?? cur.userData.cpCollision;
    const collision = readBooleanLike(rawCollision);
    if (collision !== undefined) return collision;

    const collisionOnly = readBooleanLike(cur.userData.cp_collision_only);
    if (collisionOnly === true) return true;
  }
  return undefined;
}

/**
 * Build a BVH from all collision geometry for fast spatial queries.
 * This replaces the brute-force loop over all colliders for raycasting,
 * and provides the capsule-vs-triangle collision mesh for the player controller.
 *
 * Selection policy: union of explicit-collision meshes (cp_collision=true)
 * and un-tagged structural fallback meshes. Only meshes that explicitly
 * opt out (cp_collision=false) or are flagged as effects/occluders/batched
 * are excluded. Previous logic discarded ALL fallback meshes whenever even
 * a single explicit mesh existed, which dropped structural geometry like
 * roofs, market bases, and door leaves on de_outpost and caused the player
 * to fall through or walk into visible-but-non-solid walls.
 */
export function buildCollisionBVH(scene: THREE.Scene): void {
  if (collisionMesh) {
    collisionMesh.geometry.dispose();
    collisionMesh = null;
  }
  collisionBVH = null;

  const sourceMeshes: THREE.Mesh[] = [];

  scene.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    if (obj.userData.isEffect) return;
    if (obj.userData.batchedAway) return;
    if (obj.userData.isBatched) return;
    if (obj instanceof THREE.BatchedMesh) return;
    if (obj.userData.cp_occluder === true || obj.userData.cp_occluder === 'true') return;
    if (!obj.geometry) return;

    const collisionDirective = getCollisionDirective(obj);
    if (isExplicitCollisionOptOut(collisionDirective)) return;
    if (!obj.visible && collisionDirective !== true) return;

    sourceMeshes.push(obj);
  });

  const geometries: THREE.BufferGeometry[] = [];
  for (const obj of sourceMeshes) {
    const geo = obj.geometry.clone();
    obj.updateMatrixWorld(true);
    geo.applyMatrix4(obj.matrixWorld);
    geometries.push(geo);
  }

  if (geometries.length === 0) {
    return;
  }

  const merged = mergeBufferGeometries(geometries);

  // Cleanup
  for (const geo of geometries) geo.dispose();

  if (merged) {
    collisionBVH = new MeshBVH(merged);
    merged.boundsTree = collisionBVH;
    collisionMesh = new THREE.Mesh(merged);
  }
}

/** Get the collision BVH and its mesh (for capsule-vs-BVH player collision). */
export function getCollisionBVH(): { bvh: MeshBVH; mesh: THREE.Mesh } | null {
  if (!collisionBVH || !collisionMesh) return null;
  return { bvh: collisionBVH, mesh: collisionMesh };
}

/**
 * Perform a BVH-accelerated raycast against all scene geometry.
 * Much faster than brute-force for scenes with 80k+ polygons.
 */
export function bvhRaycast(
  origin: THREE.Vector3,
  direction: THREE.Vector3,
  maxDistance = Infinity,
): { point: THREE.Vector3; normal: THREE.Vector3; distance: number } | null {
  if (!collisionBVH) return null;

  const ray = new THREE.Ray(origin, direction.clone().normalize());
  const hit = collisionBVH.raycastFirst(ray);

  if (hit && hit.distance <= maxDistance) {
    return {
      point: hit.point,
      normal: hit.face!.normal,
      distance: hit.distance,
    };
  }

  return null;
}

// ---------------------------------------------------------------------------
// Cleanup
// ---------------------------------------------------------------------------

export function disposeOcclusion(): void {
  // Tear down merged meshes: they clone geometry from originals (so dispose is
  // safe) and share material with the source meshes (so we don't dispose it
  // here — the scene-wide disposal pass owns the source material's lifetime).
  for (const mesh of batchedMeshes) {
    mesh.removeFromParent();
    mesh.geometry.dispose();
  }
  batchedMeshes.length = 0;

  zoneObjects.clear();
  multiZoneObjects.length = 0;
  activeZones = [];
  currentZone = -1;
  if (collisionMesh) {
    collisionMesh.geometry.dispose();
    collisionMesh = null;
  }
  collisionBVH = null;
}
